/*
 * Thin client for the FinSim API (apps/api). Same origin, cookie session; errors carry the
 * API's `{ code, message, ... }` body.
 */

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: Record<string, unknown> = {},
  ) {
    super(`${String(status)} ${code}`);
    this.name = 'ApiRequestError';
  }
}

/** The request never reached the API (offline, DNS, CORS…): sync should retry later. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Network error', { cause });
    this.name = 'NetworkError';
  }
}

export async function apiFetch<T>(
  path: string,
  init: Omit<RequestInit, 'body'> & { json?: unknown } = {},
): Promise<T> {
  const { json, ...rest } = init;
  const headers = new Headers(rest.headers);
  if (json !== undefined) headers.set('Content-Type', 'application/json');
  let res: Response;
  try {
    res = await fetch(`/api${path}`, {
      ...rest,
      headers,
      credentials: 'same-origin',
      ...(json === undefined ? {} : { body: JSON.stringify(json) }),
    });
  } catch (error) {
    throw new NetworkError(error);
  }
  if (res.status === 204) return undefined as T;
  const type = res.headers.get('Content-Type') ?? '';
  // A static host without the API answers with the SPA's HTML: treat it as "no API".
  if (!type.includes('application/json')) throw new NetworkError(new Error('No API'));
  const body = (await res.json()) as Record<string, unknown>;
  if (!res.ok) {
    throw new ApiRequestError(
      res.status,
      typeof body.code === 'string' ? body.code : 'internal',
      body,
    );
  }
  return body as T;
}
